import { businessQuotesService } from '@/services/quotes/business';
import type {
  CalculateAnalysisPayload,
  CalculateQuoteResponse,
} from '@/services/quotes/types';
import { clearClonedQuoteDraftMark } from '@/shared/utils/clone-quote-status.util';
import { useMutation, useQueryClient } from '@tanstack/react-query';

type Variables = {
  policyId: string;
  payload: CalculateAnalysisPayload;
};

/**
 * Recalcula uma cotação em análise aplicando alterações parciais e promove o
 * status para QuoteCalculated. Usado pelo botão "Enviar Cálculo" na tela de
 * análise.
 */
export function useCalculateAnalysis() {
  const queryClient = useQueryClient();

  return useMutation<CalculateQuoteResponse, Error, Variables>({
    mutationKey: ['quotes', 'calculate-analysis'],
    mutationFn: async ({ policyId, payload }) => {
      const response = await businessQuotesService.calculateAnalysis(
        policyId,
        payload
      );

      if (!response.success || !response.data) {
        throw new Error(
          response.error || 'Erro ao calcular cotação em análise'
        );
      }

      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['calculateQuote', variables.policyId], data);
      queryClient.invalidateQueries({
        queryKey: ['proposal-details', variables.policyId],
      });
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      // Contadores dos cards em /quotes usam queryKey separada — invalidar
      // explicitamente pra refletir o novo status sem aguardar staleTime.
      queryClient.invalidateQueries({ queryKey: ['quotes-status-count'] });
      // Limpa a marca local de "clone draft" — sem isso, `resolveQuoteListStatus`
      // continua forçando a cotação como "Em digitação" na lista /quotes
      // mesmo depois do recálculo na análise.
      clearClonedQuoteDraftMark(variables.policyId);
    },
  });
}
